import { Option, Select } from '@veeva/corgix/select';
import PropTypes from 'prop-types';
import { useContext, useEffect, useState } from 'react';
import FilterOperatorMap from './FilterOperatorMap';
import {
    filterOperatorMenuCSS,
    filterOperatorMenuCustomersPageCSS,
    filterTypeOperatorCSS,
    filterTypeOperatorCustomersPageCSS,
    filterTypeStringOperatorCSS,
} from './styles/FilterSelectionDropdownMenuContainer.styles';
import CustomerStatFilterContext from './CustomerStatFilterContext';
import pageGroupType from '../PageGroupType';

function getOperatorLabel(operator) {
    const matchingOperator = FilterOperatorMap.filter((op) => op.key === operator);
    if (matchingOperator.length) {
        return matchingOperator[0]?.label;
    }
    return null;
}

export default function FilterSelectionRowOperator({
    filterType,
    filterRowId,
    handleSetFilters,
    filter,
    filterStat,
    operator,
    setOperator,
    filterStatType,
    disabled,
}) {
    const [operatorLabel, setOperatorLabel] = useState('');
    const { pageGroup } = useContext(CustomerStatFilterContext);

    useEffect(() => {
        if (filter?.statFilter?.operator) {
            setOperator(filter.statFilter.operator);
            setOperatorLabel(getOperatorLabel(filter.statFilter.operator));
        } else if (operator) {
            setOperator(operator);
            setOperatorLabel(getOperatorLabel(operator));
        } else if (pageGroup === pageGroupType.ALL_CUSTOMERS && filter?.filterType) {
            setOperator(filter.filterType);
            setOperatorLabel(getOperatorLabel(filter.filterType));
        } else if (pageGroup === pageGroupType.ALL_CUSTOMERS) {
            setOperator('INCLUDES_ALL');
            setOperatorLabel('includes all');
        } else {
            setOperator('EQUALS');
            setOperatorLabel('=');
        }
    }, [filter, operator, setOperator, pageGroup]);

    const handleSetOperator = (e, newValue) => {
        setOperator(newValue);
        setOperatorLabel(getOperatorLabel(newValue));
        let newStatFilter;
        let statKey;
        let number;
        if (pageGroup !== pageGroupType.ALL_CUSTOMERS) {
            if (filter?.statFilter) {
                newStatFilter = {
                    operator: newValue,
                    statKey: filter.statFilter.statKey,
                    statLabel: filter.statFilter.statLabel,
                    filterType: filter.statFilter.filterType,
                    number: filter.statFilter.number,
                };
                number = filter.statFilter.number;
                statKey = filter.statFilter.statKey;
            } else {
                newStatFilter = {
                    operator: newValue,
                    statKey: filterStat.statKey,
                    statLabel: filterStat.label,
                    filterType: filterStatType,
                };
                statKey = filterStat.statKey;
            }
            handleSetFilters(
                filterRowId,
                number ? { statFilter: newStatFilter } : null,
                !!number,
                `Stats ${statKey}`,
            );
        } else if (filter?.appFamilyKeys) {
            newStatFilter = {
                appFamilyKeys: filter.appFamilyKeys,
                filterType: newValue,
            };
            handleSetFilters(filterRowId, newStatFilter, !!number, 'Application Families');
        } else if (filter?.applications) {
            newStatFilter = {
                applications: filter.applications,
                filterType: newValue,
            };
            handleSetFilters(filterRowId, newStatFilter, !!number, 'Applications');
        }
    };

    if (filterType === 'Basics') {
        return <div css={filterTypeStringOperatorCSS}>equals</div>;
    }
    if (
        filterType === 'Application' ||
        filterType === 'Customers' ||
        filterType === 'Vault Size' ||
        filterType === 'POD' ||
        filterType === 'Customer Size' ||
        filterType === 'Customer Region' ||
        filterType === 'Customer Industry' ||
        filterType === 'Customer Segment'
    ) {
        return <div css={filterTypeStringOperatorCSS}>in</div>;
    }

    if (pageGroup === pageGroupType.ALL_CUSTOMERS) {
        return (
            <div css={filterTypeOperatorCustomersPageCSS}>
                <Select
                    value={{ label: operatorLabel, value: operator }}
                    onChange={handleSetOperator}
                    css={filterOperatorMenuCustomersPageCSS}
                    syncMenuWidth
                    disabled={disabled}
                >
                    <Option key="INCLUDES_ALL" value="INCLUDES_ALL">
                        includes all
                    </Option>
                    <Option key="INCLUDES_NONE" value="INCLUDES_NONE">
                        includes none
                    </Option>
                </Select>
            </div>
        );
    }

    return (
        <div css={filterTypeOperatorCSS}>
            <Select
                value={{ label: operatorLabel, value: operator }}
                onChange={handleSetOperator}
                css={filterOperatorMenuCSS}
                syncMenuWidth
                disabled={disabled}
            >
                <Option key="EQUALS" value="EQUALS">
                    &#61;
                </Option>
                <Option key="GREATER_THAN" value="GREATER_THAN">
                    &gt;
                </Option>
                <Option key="GREATER_THAN_OR_EQUAL_TO" value="GREATER_THAN_OR_EQUAL_TO">
                    &ge;
                </Option>
                <Option key="LESS_THAN" value="LESS_THAN">
                    &lt;
                </Option>
                <Option key="LESS_THAN_OR_EQUAL_TO" value="LESS_THAN_OR_EQUAL_TO">
                    &le;
                </Option>
                <Option key="NOT_EQUALS" value="NOT_EQUALS">
                    &ne;
                </Option>
            </Select>
        </div>
    );
}

FilterSelectionRowOperator.propTypes = {
    disabled: PropTypes.bool,
    filter: PropTypes.shape({
        appFamilyKeys: PropTypes.arrayOf(PropTypes.string),
        appKeys: PropTypes.arrayOf(PropTypes.string),
        applications: PropTypes.arrayOf(PropTypes.string),
        customerIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
        customerSizes: PropTypes.arrayOf(PropTypes.string),
        filterType: PropTypes.string,
        industries: PropTypes.arrayOf(PropTypes.string),
        podNames: PropTypes.arrayOf(PropTypes.string),
        regions: PropTypes.arrayOf(PropTypes.string),
        segments: PropTypes.arrayOf(PropTypes.string),
        statFilter: PropTypes.shape({
            filterType: PropTypes.string,
            number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            operator: PropTypes.string,
            statKey: PropTypes.string,
            statLabel: PropTypes.string,
        }),
        vaultBasics: PropTypes.string,
        vaultSizes: PropTypes.arrayOf(PropTypes.string),
    }),
    filterRowId: PropTypes.number,
    filterStat: PropTypes.shape({
        compoundKey: PropTypes.string,
        label: PropTypes.string,
        statKey: PropTypes.string,
        type: PropTypes.string,
    }),
    filterStatType: PropTypes.string,
    filterType: PropTypes.string,
    handleSetFilters: PropTypes.func,
    operator: PropTypes.string,
    setOperator: PropTypes.func,
};
